/**
 * Work Log Review data-source hook — task 159c.
 *
 * Mirrors the shape used by `useApplicationsReport` and `useGroupedReport`:
 * fixture short-circuit in mockup mode, react-query + apiFetch in live
 * mode. Always passes `dateRange.start`/`dateRange.end` to the backend
 * (DESIGN-RULES Rule 35: the global TopBar picker is the only date input).
 *
 * Inputs:
 *   - `query`: URL-derived non-`dateRange` filters + sort + pagination.
 *   - `capHitMode`: when true, the fixture-mode branch returns the
 *     cap-hit variant so the page can render the "narrow your filters"
 *     hint without a real 600-group corpus. Live mode ignores this flag.
 */

import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import {
  WORK_LOG_REVIEW_FIXTURE,
  WORK_LOG_REVIEW_CAP_HIT_FIXTURE,
  WORK_LOG_REVIEW_GROUP_CAP_DEFAULT,
} from '@/data/workLogReview';
import type { WorkLogReviewQuery, WorkLogReviewResponse } from '@/types/workLogReview';
import { computeFixtureResponse } from './workLogReviewFixture';

interface UseWorkLogReviewArgs extends WorkLogReviewQuery {
  capHitMode?: boolean;
}

interface UseWorkLogReviewResult {
  data: WorkLogReviewResponse | null;
  isLoading: boolean;
}

function buildQueryString(args: WorkLogReviewQuery): string {
  const qs = new URLSearchParams();
  if (args.from) qs.set('from', args.from);
  if (args.to) qs.set('to', args.to);
  for (const src of args.classificationSource ?? []) qs.append('classificationSource', src);
  for (const id of args.employeeIds ?? []) qs.append('employeeIds', id);
  if (args.businessUnitId) qs.set('businessUnitId', args.businessUnitId);
  if (args.classifiedByRuleId) qs.set('classifiedByRuleId', args.classifiedByRuleId);
  if (args.projectId) qs.set('projectId', args.projectId);
  if (args.clientId) qs.set('clientId', args.clientId);
  for (const dim of args.groupBy ?? []) qs.append('groupBy', dim);
  if (args.sortBy) qs.set('sortBy', args.sortBy);
  if (args.sortDir) qs.set('sortDir', args.sortDir);
  if (args.page) qs.set('page', String(args.page));
  if (args.pageSize) qs.set('pageSize', String(args.pageSize));
  return qs.toString();
}

export function useWorkLogReview(args: UseWorkLogReviewArgs): UseWorkLogReviewResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const liveQuery: WorkLogReviewQuery = {
    ...args,
    from: dateRange.start || undefined,
    to: dateRange.end || undefined,
  };

  const query = useQuery<WorkLogReviewResponse>({
    queryKey: ['workLogReview', orgId, liveQuery],
    queryFn: async () => {
      const token = await getToken();
      const qs = buildQueryString(liveQuery);
      return apiFetch<WorkLogReviewResponse>(
        `/api/v1/orgs/${orgId}/work-log-review${qs ? `?${qs}` : ''}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!dateRange.start && !!dateRange.end,
  });

  if (mockupMode) {
    const source = args.capHitMode ? WORK_LOG_REVIEW_CAP_HIT_FIXTURE : WORK_LOG_REVIEW_FIXTURE;
    const data = computeFixtureResponse(
      { source, groupCap: WORK_LOG_REVIEW_GROUP_CAP_DEFAULT },
      args,
    );
    return { data, isLoading: false };
  }
  return { data: query.data ?? null, isLoading: query.isLoading };
}
